import { Tooltip } from '@mui/material'
import { postStat } from 'api/stats/stats.api'
import clsx from 'clsx'
import { getRoute } from 'helpers/route.helper/route.helper'
import { useActiveTags } from 'hooks/filterHooks/filterHooks'
import React, { ReactElement, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import filterStore from 'store/filter/filter'
import Icon from 'ui/atoms/Icon/Icon'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import FilterComponent from 'ui/components/Filter/FilterComponent'
import { ToggleButton } from 'ui/molecules/ToggleButton/ToggleButton'
import { useSnapshot } from 'valtio'

type FilterModuleContentProps = {
  portalId?: string
}

export default function FilterModuleContent({ portalId }: FilterModuleContentProps): ReactElement | null {
  const { t } = useTranslation()
  const { hasAccess } = useAuth()

  const { filterComponentPerRoute, tagsPerUiconfig } = useSnapshot(filterStore)
  const [filterIsCollapsed, setFilterCollapsed] = useState<boolean>(true)
  const route = getRoute()

  const { activeTags, isActiveTagsFiltered } = useActiveTags({
    availableFiltersSubset: tagsPerUiconfig,
  })

  const routeHasFilterComponent = filterComponentPerRoute[route]
  const hasFilterData = activeTags.size > 0

  // Filter button is feature flagged
  const showFilterButton = useMemo(() => {
    if (!hasAccess({ submodule: 'optimize_filter', module: 'optimize' })) {
      return false
    }

    return routeHasFilterComponent
  }, [hasAccess, routeHasFilterComponent])

  const filterIcon = useMemo(() => {
    return isActiveTagsFiltered ? (
      <Icon tooltip={{ title: t('Filters active'), placement: 'right' }} icon="fas fa-filter" />
    ) : (
      ''
    )
  }, [t, isActiveTagsFiltered])

  const toggleFilterCollapse = (): void => {
    postStat('/optimize', 'toggle-filter')
    setFilterCollapsed(!filterIsCollapsed)
  }

  const portalElement = portalId ? document.getElementById(portalId) : null
  const filterContent = !filterIsCollapsed && (
    <div
      className={clsx({
        'absolute px-2 py-3 z-50 bg-white shadow-md left-2 right-2 border border-primary-5 rounded-md': !portalElement,
      })}
    >
      <FilterComponent availableFiltersSubset={tagsPerUiconfig} activeTags={activeTags} />
    </div>
  )

  if (!showFilterButton) {
    return null
  }

  return (
    <div>
      <Tooltip
        title={
          !hasFilterData ? t('No data to filter. Try reloading the page or open up a card if all are collapsed') : ''
        }
        placement="bottom"
        arrow
      >
        <div className="">
          <ToggleButton isCollapsed={filterIsCollapsed} onClick={toggleFilterCollapse} disabled={!hasFilterData}>
            <span>
              {' '}
              {t('Filter')}
              {filterIcon}
            </span>
          </ToggleButton>
        </div>
      </Tooltip>

      {portalElement ? createPortal(filterContent, portalElement) : filterContent}
    </div>
  )
}
